import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideEnvironmentInitializer,
    provideZoneChangeDetection,
} from '@angular/core';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { PreloadAllModules, provideRouter, withComponentInputBinding, withPreloading } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { TranslateCompiler, TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { HeaderService, OidcSettings, provideOneGoHeader } from 'onego-header';

import { environment } from '../environments/environment';
import { AppInitializerFactory, TranslateLoaderFactory } from './factories';
import { AppSettings } from './models/app-settings.model';
import { CustomDateAdapter } from './mtp-common/adapters/custom-date.adapter';
import {
    ErrorInterceptor,
    LanguageInterceptor,
    TenantInterceptor,
    UnauthorizedInterceptor,
} from './mtp-common/interceptors';
import { AuthImagePipe } from './mtp-common/pipes/auth-image.pipe';
import { FromLocaleStringMapPipe } from './mtp-common/pipes/from-locale-string-map.pipe';
import { FuzzySearchPipe } from './mtp-common/pipes/fuzzy-search.pipe';
import { SortPipe } from './mtp-common/pipes/sort.pipe';
import { AppSettingsService, GeolocatorService, SocketService, UpdaterService } from './mtp-common/services';
import { SOCKET_EVENT_HANDLERS, socketEventHandlerProviders } from './mtp-common/services/socket-event-handlers';
import { AppStateService } from './mtp-common/services/state';
import { routes } from './mtp-routing/mtp-routing.routes';

registerLocaleData(localeNl);

export const languageConfig = {
    availableLangs: environment.supportedLocales,
    defaultLang: environment.defaultLocale,
    fallbackLang: environment.fallbackLocale,
};

const pipes = [AuthImagePipe, FromLocaleStringMapPipe, FuzzySearchPipe, SortPipe];

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideRouter(routes, withPreloading(PreloadAllModules), withComponentInputBinding()),
        provideOneGoHeader((appSettings: unknown) => {
            const { oidcConfig, url } = appSettings as AppSettings & OidcSettings;
            return {
                keycloakUrl: oidcConfig.keycloakUrl,
                keycloakRealm: oidcConfig.keycloakRealm,
                keycloakClientId: oidcConfig.keycloakClientId,
                keycloakScope: oidcConfig.scope,
                redirectUri: window.location.origin,
                userApi: url.api.users,
                tenantsApi: url.api.tenants,
                taskApi: url.api.tasks,
                jwtDomainWhitelist: url.jwtDomainWhitelist,
            };
        }, languageConfig),
        importProvidersFrom(
            ToastrModule.forRoot(),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: TranslateLoaderFactory,
                    deps: [],
                },
                compiler: {
                    provide: TranslateCompiler,
                    useClass: TranslateMessageFormatCompiler,
                },
            }),
            ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.env !== 'DEV' }),
            MatNativeDateModule
        ),
        // HTTP interceptors (kept DI-based; consumed by onego-header's withInterceptorsFromDi())
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LanguageInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TenantInterceptor,
            multi: true,
        },
        { provide: DateAdapter, useClass: CustomDateAdapter },
        ...pipes,
        ...socketEventHandlerProviders,
        DatePipe,
        // Eagerly instantiate the always-on services and every socket event
        // handler (through the SOCKET_EVENT_HANDLERS multi-token) so their
        // subscriptions are live for the lifetime of the app.
        provideEnvironmentInitializer(() => {
            inject(AppSettingsService);
            inject(TranslateService);
            inject(UpdaterService);
            inject(GeolocatorService);
            inject(SocketService);
            inject(SOCKET_EVENT_HANDLERS);
        }),
        provideAppInitializer(() =>
            AppInitializerFactory(
                inject(HeaderService),
                inject(TranslateService),
                inject(AppSettingsService),
                inject(AppStateService)
            )()
        ),
    ],
};
